import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, AreaChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ComposedChart, ResponsiveContainer, Tooltip, Legend, XAxis, YAxis,
  CartesianGrid, Cell
} from 'recharts';
import { Maximize2, Minimize2, Download, Image } from 'lucide-react';

const CHART_COLORS = [
  '#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#f97316', '#ec4899', '#14b8a6', '#84cc16',
];

const chartComponents = {
  line: { Chart: LineChart, Series: Line },
  bar: { Chart: BarChart, Series: Bar },
  pie: { Chart: PieChart, Series: Pie },
  area: { Chart: AreaChart, Series: Area },
  radar: { Chart: RadarChart, Series: Radar },
  composed: { Chart: ComposedChart, Series: Bar },
};

export default function ReportChart({
  type = 'bar',
  data = [],
  dataKeys = [],
  xAxisKey = 'name',
  title,
  height = 350,
  loading = false,
  onExpand,
}) {
  const [hiddenSeries, setHiddenSeries] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const chartRef = useRef(null);

  const chartConfig = chartComponents[type] || chartComponents.bar;
  const ChartComponent = chartConfig.Chart;

  const handleLegendClick = useCallback((entry) => {
    const key = entry.dataKey || entry.value;
    setHiddenSeries((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev]
    );
  }, []);

  const exportAsImage = async () => {
    const el = chartRef.current?.querySelector('.recharts-wrapper');
    if (!el) return;
    const canvas = await import('html2canvas');
    const img = await canvas.default(el);
    const link = document.createElement('a');
    link.download = `chart-${Date.now()}.png`;
    link.href = img.toDataURL('image/png');
    link.click();
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-4" />
        <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
      </div>
    );
  }

  const renderChart = () => {
    if (type === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              dataKey={dataKeys[0] || 'value'}
              nameKey={xAxisKey}
              cx="50%"
              cy="50%"
              outerRadius={100}
              innerRadius={50}
              animationBegin={0}
              animationDuration={800}
              animationEasing="ease-out"
            >
              {data.map((_, idx) => (
                <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(255,255,255,0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Legend
              onClick={handleLegendClick}
              wrapperStyle={{ fontSize: '11px', cursor: 'pointer' }}
            />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (type === 'radar') {
      return (
        <ResponsiveContainer width="100%" height={height}>
          <RadarChart data={data}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey={xAxisKey} tick={{ fontSize: 11 }} />
            <PolarRadiusAxis tick={{ fontSize: 10 }} />
            {dataKeys.map((key, idx) => (
              <Radar
                key={key}
                name={key}
                dataKey={key}
                stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                fill={CHART_COLORS[idx % CHART_COLORS.length]}
                fillOpacity={0.2}
                animationBegin={0}
                animationDuration={800}
                animationEasing="ease-out"
              />
            ))}
            <Tooltip
              contentStyle={{
                backgroundColor: 'rgba(255,255,255,0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Legend
              onClick={handleLegendClick}
              wrapperStyle={{ fontSize: '11px', cursor: 'pointer' }}
            />
          </RadarChart>
        </ResponsiveContainer>
      );
    }

    const commonProps = {
      data,
      margin: { top: 10, right: 10, left: 0, bottom: 0 },
    };

    return (
      <ResponsiveContainer width="100%" height={height}>
        <ChartComponent {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey={xAxisKey}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickLine={false}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(255,255,255,0.95)',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '12px',
            }}
          />
          <Legend
            onClick={handleLegendClick}
            wrapperStyle={{ fontSize: '11px', cursor: 'pointer' }}
          />
          {dataKeys.map((key, idx) => {
            if (hiddenSeries.includes(key)) return null;
            const Component = chartConfig.Series;
            return (
              <Component
                key={key}
                type="monotone"
                dataKey={key}
                stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                fill={CHART_COLORS[idx % CHART_COLORS.length]}
                fillOpacity={0.15}
                strokeWidth={2}
                dot={{ r: 3, fill: CHART_COLORS[idx % CHART_COLORS.length] }}
                activeDot={{ r: 5 }}
                animationBegin={0}
                animationDuration={800}
                animationEasing="ease-out"
              />
            );
          })}
        </ChartComponent>
      </ResponsiveContainer>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm ${
        expanded ? 'fixed inset-4 z-50 overflow-auto' : ''
      }`}
      ref={chartRef}
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title || `${type.charAt(0).toUpperCase() + type.slice(1)} Chart`}</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={exportAsImage}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Export as image"
          >
            <Image size={14} />
          </button>
          <button
            onClick={() => {
              setExpanded(!expanded);
              onExpand?.(!expanded);
            }}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title={expanded ? 'Minimize' : 'Expand'}
          >
            {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </div>
      </div>

      <div className="p-5">
        {data.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
            <Image size={36} className="mb-2" />
            <p className="text-sm font-medium">No data available</p>
          </div>
        ) : (
          renderChart()
        )}
      </div>
    </motion.div>
  );
}
